import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { getAllDetails, postAllData } from '../../service/ApiService';
import { GET_DASHBOARD_DATA, CONFIRM_EMP_PERF_PASS } from '../../config/ApiConstant';
import { validatePasswordField } from '../../config/Validate';
import moment from 'moment';

type DateFilterMode = 'current_month | last_month' | 'last_3_months' | 'last_6_months' | 'last_year' | 'custom' | any;

const Dashboard = () => {
  document.title = "Dashboard | Nagra Accounting";
  const toast = useRef<any>(null);

  const navigate = useNavigate();

  // get userdata from local storage
  const rawUserData = localStorage.getItem("user_data");

  let userData: any = null;
  // parse rawuserdata into userData
  userData = rawUserData ? JSON.parse(rawUserData) : null;
  const userRole = userData?.role || "";

  const [dashboardData, setDashboardData] = useState<any>({});
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [chartData, setChartData] = useState<any>(null);
  const [chartOptions, setChartOptions] = useState({});
  const [chartKey, setChartKey] = useState<number>(0);
  const [chartDateMode, setChartDateMode] = useState<DateFilterMode>('current_month');
  const [customDateRange, setCustomDateRange] = useState<Date[] | null>(null);
  const [showEmployeeTable, setShowEmployeeTable] = useState<boolean>(false);
  const [visiblePasswordDialog, setVisiblePasswordDialog] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [verifyLoading, setVerifyLoading] = useState<boolean>(false);


  // compute start/end date strings for a given filter mode
  const getDateRangeForMode = (
    mode: string,
    customRange: Date[] | null
  ): { start: string; end: string } => {
    let start = '';
    let end = '';

    if (mode === 'current_month') {
      start = moment().startOf('month').format('YYYY-MM-DD');
      end = moment().endOf('month').format('YYYY-MM-DD');

    } else if (mode === 'last_month') {
      start = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
      end = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

    } else if (mode === 'last_3_months') {
      start = moment().subtract(2, 'months').startOf('month').format('YYYY-MM-DD');
      end = moment().endOf('month').format('YYYY-MM-DD');

    } else if (mode === 'last_6_months') {
      start = moment().subtract(5, 'months').startOf('month').format('YYYY-MM-DD');
      end = moment().endOf('month').format('YYYY-MM-DD');

    } else if (mode === 'last_year') {
      start = moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD');
      end = moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD');

    } else if (
      mode === 'custom' &&
      customRange &&
      customRange[0] &&
      customRange[1]
    ) {
      start = moment(customRange[0]).format('YYYY-MM-DD');
      end = moment(customRange[1]).format('YYYY-MM-DD');
    } else if (mode === 'All') {
      start = '2026-01-01';
      end = moment().format('YYYY-MM-DD');
    }

    return { start, end };
  };

  // get dashboard data
  const getDashboard = async (startDate?: string, endDate?: string, mode?: any) => {
    try {
      let url = GET_DASHBOARD_DATA;
      let queryParams = [];
      if (startDate && endDate) {
        queryParams.push(`start_date=${startDate}`);
        queryParams.push(`end_date=${endDate}`);
      }
      if (mode) {
        queryParams.push(`filter_by=${mode === 'all' ? 'All' : mode}`);
      }

      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }
      const response = await getAllDetails(url);
      const data = response;

      if (data) {
        setDashboardData(data);
        setPageLoad(true);
      } else {
        setDashboardData({});
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // clear all filters
  const clearAll = () => {
    setChartDateMode(null);
    setCustomDateRange(null);
  };

  useEffect(() => {
    localStorage.removeItem('taxpayer_selected_date');
  }, []);

  useEffect(() => {
    const { start, end } = getDateRangeForMode(chartDateMode, customDateRange);

    if (start && end) {
      getDashboard(start, end, chartDateMode);
    } else {
      getDashboard(undefined, undefined, chartDateMode);
    }
  }, [chartDateMode, customDateRange]);

  useEffect(() => {
    if (!dashboardData?.tax_payer_status) return;

    const status = dashboardData.tax_payer_status;

    const labels = Object.keys(status).map((label) =>
      label
        .replace("Missing Document/Information", "Missing Docs")
        .replace("Document Received", "Docs Received")
        .replace("Sign Done Whatsapp", "Sign Done WA")
    );

    const values = Object.values(status);

    const newChartData = {
      labels,
      datasets: [
        {
          label: "Taxpayers",
          data: values,
          backgroundColor: "#2563eb",
          borderRadius: 6,
          barThickness: 35
        }
      ]
    };

    setChartData(newChartData);
    setChartKey(prev => prev + 1); // force Chart.js to fully remount with new data

    setChartOptions({
      maintainAspectRatio: false,
      aspectRatio: 1.2, // smaller value = taller chart
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true
        }
      },
      onClick: (e: any, elements: any[]) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const statusLabel = labels[index];

          // Map label back to original key if needed
          const originalStatus = Object.keys(status)[index];

          const { start, end } = getDateRangeForMode(chartDateMode, customDateRange);
          navigate('/taxpayer-status', {
            state: {
              status: originalStatus,
              ...(chartDateMode === 'All' ? { filter_by: 'All' } : { start_date: start, end_date: end })
            }
          });
        }
      },
      scales: {
        x: {
          ticks: {
            font: {
              size: 11
            },
            maxRotation: 0,
            minRotation: 0
          },
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1
          },
          grid: {
            color: "#f1f5f9"
          }
        }
      }
    });
  }, [dashboardData]);

  // verify password for employee table
  const handleVerifyPassword = async () => {
    try {
      const { errors, isError } = validatePasswordField(password);
      setPasswordError(errors.password);
      if (isError) return;

      setVerifyLoading(true);
      const payload = { password };
      const response = await postAllData(CONFIRM_EMP_PERF_PASS, payload);

      if (response) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Verification successful',
        });
        setShowEmployeeTable(true);
        setPassword('');
        setVisiblePasswordDialog(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error?.response?.data?.message || 'Verification failed',
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  const closePasswordDialog = () => {
    setVisiblePasswordDialog(false);
    setPassword('');
    setPasswordError(null);
  };

  return (
    <>
      <AppBreadcrumb currentMenu={"Dashboard"} pageName={"Dashboard"} pageTitle="Dashboard" />
      <Toast ref={toast} />
      <div className="layout-dashboard">
        <div className="grid mt-2">
          {/* Total Employees and total Tax payer */}
          <>
            {/* total Employees */}
            {userRole === "Admin" && (
              <>
                <div className="col-12 md:col-6 lg:col-4">
                  <Link to="/employees">
                    <div className="card dashbaord-main-box">
                      <div className="small-box bg-info">
                        <div className="inner" style={{ borderLeft: "3px solid #dc3545" }}>
                          <div className="flex flex-column justify-content-between">
                            <h2 className="mb-1 title-text">Total Employees</h2>
                            <div className="flex align-items-center justify-content-between">
                              <h3 className="mt-0 mb-0 num-text" style={{ color: "#dc3545" }}>
                                {pageLoad ? (
                                  !window.cn(dashboardData) && !window.cn(dashboardData.employee_count) ? (
                                    dashboardData.employee_count
                                  ) : (
                                    ""
                                  )
                                ) : (
                                  <Skeleton width="5rem" height="2.5rem" className="rounded" />
                                )}
                              </h3>
                              <div
                                className="dashboard-icon"
                                style={{
                                  padding: "0.75rem",
                                  backgroundColor: "#f8d7da",
                                  borderRadius: "9999px",
                                  color: "#dc3545",
                                  width: "50px",
                                  height: "50px",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="lucide lucide-users"
                                >
                                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                  <path d="M16 3.128a4 4 0 0 1 0 7.744" />
                                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                  <circle cx="9" cy="7" r="4" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </>
            )}

            {/* total tax payer list */}
            <div className="col-12 md:col-6 lg:col-4">
              <Link to="/tax-payer-list">
                <div className="card dashbaord-main-box">
                  <div className="small-box bg-info">
                    <div className="inner" style={{ borderLeft: "3px solid #35b653" }}>
                      <div className="flex flex-column justify-content-between">
                        <h2 className="mb-1 title-text">Total Tax Form</h2>
                        <div className="flex align-items-center justify-content-between">
                          <h3 className="mt-0 mb-0 num-text" style={{ color: "#35b653" }}>
                            {pageLoad ? (
                              !window.cn(dashboardData) && !window.cn(dashboardData.tax_payer_list) ? (
                                dashboardData.tax_payer_list
                              ) : (
                                ""
                              )
                            ) : (
                              <Skeleton width="5rem" height="2.5rem" className="rounded" />
                            )}
                          </h3>
                          <div
                            className="dashboard-icon"
                            style={{
                              padding: "0.75rem",
                              backgroundColor: "#d7f0dd",
                              borderRadius: "9999px",
                              color: "#35b653",
                              width: "50px",
                              height: "50px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-building2"
                            >
                              <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                              <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                              <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
                              <path d="M10 6h4" />
                              <path d="M10 10h4" />
                              <path d="M10 14h4" />
                              <path d="M10 18h4" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <div className="card dashbaord-main-box">
                <div className="small-box bg-info">
                  <div className="inner" style={{ borderLeft: "3px solid #7a35b6" }}>
                    <div className="flex flex-column justify-content-between">
                      <h2 className="mb-1 title-text">Completed Payments by Tax Payer</h2>

                      <div className="flex align-items-center justify-content-between">
                        <h3 className="mt-0 mb-0 num-text" style={{ color: "#7a35b6" }}>
                          {pageLoad ? (
                            !window.cn(dashboardData) &&
                              !window.cn(dashboardData.payment_completed_tax_payer_count) ? (
                              dashboardData.payment_completed_tax_payer_count
                            ) : (
                              ""
                            )
                          ) : (
                            <Skeleton width="5rem" height="2.5rem" className="rounded" />
                          )}
                        </h3>

                        <div
                          className="dashboard-icon"
                          style={{
                            padding: "0.75rem",
                            backgroundColor: "#7a35b61a",
                            borderRadius: "9999px",
                            color: "#7a35b6",
                            width: "50px",
                            height: "50px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-credit-card"
                          >
                            <rect width="20" height="14" x="2" y="5" rx="2" />
                            <line x1="2" x2="22" y1="10" y2="10" />
                          </svg>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* monthly revenue */}
            {userRole == "Admin" && (
              <div className="col-12 md:col-6 lg:col-4">
                <div className="card dashbaord-main-box">
                  <div className="small-box bg-info">
                    <div className="inner" style={{ borderLeft: "3px solid #730d56" }}>
                      <div className="flex flex-column justify-content-between">
                        <h2 className="mb-1 title-text">Monthly Revenue</h2>

                        <div className="flex align-items-center justify-content-between">
                          <h3 className="mt-0 mb-0 num-text" style={{ color: "#730d56" }}>
                            $
                            {pageLoad ? (
                              !window.cn(dashboardData) &&
                                !window.cn(dashboardData.monthly_revenue) ? (
                                dashboardData.monthly_revenue.toFixed(2)
                              ) : (
                                ""
                              )
                            ) : (
                              <Skeleton width="5rem" height="2.5rem" className="rounded" />
                            )}
                          </h3>

                          <div
                            className="dashboard-icon"
                            style={{
                              padding: "0.75rem",
                              backgroundColor: "#730d561a",
                              borderRadius: "9999px",
                              color: "#730d56",
                              width: "50px",
                              height: "50px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-dollar-sign"
                            >
                              <line x1="12" x2="12" y1="2" y2="22"></line>
                              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7H14.5a3.5 3.5 0 0 1 0 7H7"></path>
                            </svg>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* pending file list */}
            <div className="col-12 md:col-6 lg:col-4">
              <Link to="/pending-file-list">
                <div className="card dashbaord-main-box">
                  <div className="small-box bg-info">
                    <div className="inner" style={{ borderLeft: "3px solid #2d39a1" }}>
                      <div className="flex flex-column justify-content-between">
                        <h2 className="mb-1 title-text">Pending Tax Forms</h2>
                        <div className="flex align-items-center justify-content-between">
                          <h3 className="mt-0 mb-0 num-text" style={{ color: "#2d39a1" }}>
                            {pageLoad ? (
                              !window.cn(dashboardData) && !window.cn(dashboardData.pending_list_count) ? (
                                dashboardData.pending_list_count
                              ) : (
                                ""
                              )
                            ) : (
                              <Skeleton width="5rem" height="2.5rem" className="rounded" />
                            )}
                          </h3>
                          <div
                            className="dashboard-icon"
                            style={{
                              padding: "0.75rem",
                              backgroundColor: "#2d39a11a",
                              borderRadius: "9999px",
                              color: "#2d39a1",
                              width: "50px",
                              height: "50px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-file-clock"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <path d="M14 2v6h6" />

                              <circle cx="12" cy="15" r="3" />
                              <path d="M12 14v2l1 1" />
                            </svg>

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            {/* total company list */}
            <div className="col-12 md:col-6 lg:col-4">
              <Link to="/company-list">
                <div className="card dashbaord-main-box">
                  <div className="small-box bg-info">
                    <div className="inner" style={{ borderLeft: "3px solid #a42980ff" }}>
                      <div className="flex flex-column justify-content-between">
                        <h2 className="mb-1 title-text">Total companies</h2>
                        <div className="flex align-items-center justify-content-between">
                          <h3 className="mt-0 mb-0 num-text" style={{ color: "#a42980ff" }}>
                            {pageLoad ? (
                              !window.cn(dashboardData) && !window.cn(dashboardData.companies_count) ? (
                                dashboardData.companies_count
                              ) : (
                                ""
                              )
                            ) : (
                              <Skeleton width="5rem" height="2.5rem" className="rounded" />
                            )}
                          </h3>
                          <div
                            className="dashboard-icon"
                            style={{
                              padding: "0.75rem",
                              backgroundColor: "#a429801a",
                              borderRadius: "9999px",
                              color: "#a42980ff",
                              width: "50px",
                              height: "50px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect x="6" y="3" width="12" height="18" rx="2" />

                              {/* windows */}
                              <path d="M9 7h2M13 7h2" />
                              <path d="M9 11h2M13 11h2" />
                              <path d="M9 15h2M13 15h2" />

                              {/* bottom base line */}
                              <path d="M4 21h16" />
                            </svg>

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </>
        </div>
        {/* Shared date filter for both Taxpayer Status chart and Tax Filed by Employee table */}
        <div className='flex align-items-center justify-content-end flex-wrap mb-3' style={{ gap: '0.75rem' }}>
          <div className='flex align-items-center flex-wrap' style={{ gap: '0.5rem' }}>
            <Dropdown
              value={chartDateMode}
              options={[
                { label: 'Current Month', value: 'current_month' },
                { label: 'Last Month', value: 'last_month' },
                { label: 'Last 3 Months', value: 'last_3_months' },
                { label: 'Last 6 Months', value: 'last_6_months' },
                { label: 'Year', value: 'last_year' },
                { label: 'Custom Date', value: 'custom' },
                { label: 'All', value: 'all' },
              ]}
              onChange={(e) => {
                setChartDateMode(e.value ?? null);
                if (e.value !== 'custom') setCustomDateRange(null);
              }}
              placeholder='Select Period'
              style={{ minWidth: '160px' }}
            />
            {chartDateMode === 'custom' && (
              <Calendar
                value={customDateRange as any}
                onChange={(e) => {
                  const val = e.value as Date[];
                  setCustomDateRange(val);
                }}
                selectionMode='range'
                readOnlyInput
                dateFormat='dd/mm/yy'
                placeholder='Select Date Range'
                showIcon
                appendTo={document.body}
                style={{ minWidth: '220px' }}
              />
            )}
            <Button
              icon="pi pi-filter-slash"
              label="Clear"
              onClick={clearAll}
              className="ml-3 p-button-outlined common-p-outline-button"
            />
          </div>
        </div>

        {/* Taxpayer Status chart */}
        <div className='flex gap-4'>
          <div className='card lg:col-8 p-4' style={{ boxShadow: '0px 0px 6px 2px #d8dde6' }}>
            <h3 className='mb-3'>Taxpayer Status</h3>
            <div className='inner'>
              {chartData && <Chart key={chartKey} type='bar' data={chartData} options={chartOptions} />}
            </div>

            {/* Status Counters */}
            {dashboardData?.tax_payer_status && (
              <div className='flex flex-wrap gap-2 mt-3'>
                {Object.entries(dashboardData.tax_payer_status).map(([statusName, count]) => (
                  <div
                    key={statusName}
                    className='flex border-1 border-gray-200 border-round-3xl px-3 py-2 align-items-center justify-content-between cursor-pointer hover:bg-gray-50 transition-colors'
                    style={{ flex: '0 0 207px' }}
                    onClick={() => {
                      const { start, end } = getDateRangeForMode(chartDateMode, customDateRange);
                      navigate('/taxpayer-status', { state: { status: statusName, ...(chartDateMode === 'All' ? { filter_by: 'All' } : { start_date: start, end_date: end }) } });
                    }}
                  >
                    <div className='font-medium text-gray-600' style={{ fontSize: '0.8rem' }}>
                      {statusName}
                    </div>
                    <div className='font-bold text-primary flex align-items-center justify-content-center bg-blue-50 px-2' style={{ fontSize: '0.85rem', borderRadius: '12px', height: '24px', minWidth: '24px' }}>
                      {count as number}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tax Filed by Employee table */}
          {userRole === 'Admin' && (
            <div className='card lg:col-4 p-4' style={{ boxShadow: '0px 0px 6px 2px #d8dde6' }}>
              <div className="flex align-items-center justify-content-between mb-3">
                <h3 className='m-0'>Tax Filed by Employee</h3>
                <Button
                  icon={showEmployeeTable ? "pi pi-eye-slash" : "pi pi-eye"}
                  className="p-button-text p-button-sm p-0 m-0"
                  style={{ width: '2rem', height: '2rem' }}
                  onClick={() => {
                    if (showEmployeeTable) {
                      setShowEmployeeTable(false);
                    } else {
                      setVisiblePasswordDialog(true);
                    }
                  }}
                  tooltip={showEmployeeTable ? "Hide Data" : "Show Data"}
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
              {showEmployeeTable ? (
                <DataTable
                  value={dashboardData?.tax_filed_by_data || []}
                  stripedRows
                  showGridlines
                  responsiveLayout='scroll'
                >
                  <Column field='employee_name' header='Employee Name' />
                  <Column
                    field='count'
                    header='Tax Filed'
                    bodyStyle={{ textAlign: 'center' }}
                  />
                </DataTable>
              ) : (
                <div
                  onClick={() =>
                    showEmployeeTable
                      ? setShowEmployeeTable(false)
                      : setVisiblePasswordDialog(true)
                  }
                  className="flex flex-column cursor-pointer align-items-center justify-content-center py-5 text-400 bg-gray-50 border-round border-dashed border-200"
                >
                  <i className="pi pi-lock mb-2" style={{ fontSize: '1.5rem' }}></i>
                  <span className="text-sm font-medium">
                    Content is password protected
                  </span>
                  <span className="text-sm font-medium">
                    Click here to view
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <Dialog
          visible={visiblePasswordDialog}
          header="Verification Required"
          modal
          className="mx-3 lg:mx-0"
          style={{ width: "400px" }}
          onHide={closePasswordDialog}
          footer={
            <div className="flex pt-2 align-items-center justify-content-end">
              <Button label="Cancel" icon="pi pi-times" className="p-button-secondary p-button-text" onClick={closePasswordDialog} />
              <Button label="Verify" icon="pi pi-check" className="common-primary-button ml-2" loading={verifyLoading} onClick={handleVerifyPassword} />
            </div>
          }
        >
          <div className="grid p-fluid formgrid mt-1">
            <div className="field col-12">
              <label className="font-medium mb-2 block">Enter Password to view table data:</label>
              <Password
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                }}
                toggleMask
                feedback={false}
                autoComplete="off"
                className={passwordError ? "p-invalid w-full" : "w-full"}
                inputClassName="w-full"
                placeholder="Enter Password"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleVerifyPassword();
                  }
                }}
              />
              {passwordError && <small className="p-error">{passwordError}</small>}
            </div>
          </div>
        </Dialog>
      </div>
    </>
  );
};

export default Dashboard;
